import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty, remove } from 'lodash';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { GpsFixed, ExpandMore } from '@mui/icons-material';
import FormHelperText from '@mui/material/FormHelperText';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";

import Strategy from '../../components/placement/Strategy';
import DeviceTypes from '../../components/placement/DeviceTypes';
import SalesProduct from '../../components/placement/SalesProduct';
import StrategyDetails from '../../components/placement/StrategyDetails';
import ExcludedModels from '../../components/placement/ExcludedModels';
import CreativeProducts from '../../components/placement/CreativeProducts';
import PubSelect from '../../components/placement/PubSelect';

import { isDelivering, isPushed } from '../../utils/adManagerHelpers';
import { hasPubSelect } from '../../utils/hasPubSelect';
import GenericTargeting from "../../components/placement/GenericTargeting";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";

class Targeting extends Component {

  constructor(props) {
    super(props);
    this.state = {
      incExc: 'INCLUDE',  // INCLUDE or EXCLUDE
      excludeModels: false,
      pubsCleared: false
    };
    this.canExcludeModels = false;
  }

  getPresetTypeId = (strategyId, salesProductId) => {
    const { strategies, salesProducts } = this.props;
    if (!strategies || strategies.length === 0 || !salesProducts || salesProducts.length === 0) {
      return 'unused';
    }
    const selectedStrategy = strategies.find(s => s.id === strategyId);
    const selectedSalesProduct = salesProducts.find(s => s.id === salesProductId);
    if (isEmpty(selectedStrategy) || isEmpty(selectedSalesProduct)) {
      return 'unused';
    }
    if (selectedSalesProduct.name === 'JAG Awareness' && selectedStrategy.name === 'Suggestive') {
      return 'unused';
    }
    return selectedStrategy.RequiredDetailTypeId || 'unused';
  };

  strategyChanged = evt => {
    const { strategies, placement } = this.props;
    const strategyId = evt.target.value;
    const copy = Object.assign({}, placement);
    copy.StrategyId = strategyId;
    copy.Strategy = strategies.find(s => s.id === strategyId);
    if (this.getPresetTypeId(strategyId, placement.SalesProductId) !== this.state.presetTypeId) {
      copy.CustomCriteriaPresets = [];
    }
    this.props.onTargetingChange('StrategyId', strategyId, copy);
  };

  salesProductChanged = evt => {
    const { salesProducts, placement } = this.props;
    const spid = evt.target.value;
    const copy = Object.assign({}, placement);
    copy.SalesProductId = spid;
    copy.SalesProduct = salesProducts.find(sp => sp.id === spid);
    copy.pubs = [];
    copy.Pubs = [];
    this.props.onTargetingChange('SalesProductId', spid, copy);
  };

  tptChanged = evt => {
    this.props.onChange(evt);
  }

  geoGlobalChange = (evt) => {
    const { onChange, placement } = this.props;
    const geoGlobal = placement.geoGlobal;
    const event = { target: { name: 'geoGlobal', value: { global: geoGlobal } } };
    onChange(event);
  }

  excludeChange = () => {
    const excludeModels = !this.state.excludeModels;
    this.setState({ excludeModels });
  }

  /**
   * Two special fixes: one for Canada, one for flat-fee packages. Then:
   * Strategy ==> SalesProduct,
   * Strategy, SalesProduct => Presets
   * Check if current sales product choice is still an option, if not remove it.
   * Check the Strategy-SalesProduct combination for invalid CreativeProducts and remove them.
   * SalesProduct, DeviceTypes ==> CreativeProducts
   */
  manageCascade(order, placement, strategies, salesProducts, presets, creativeProducts, pkg, constants) {
    const doExclusions = true;
    const targeted = { strategies: [], salesProducts: [], presets: [], creativeProducts: [] };
    if (order && !isEmpty(strategies)) {
      targeted.strategies = strategies;
      if (order.Advertiser && order.Advertiser.countryCode === 'CDN') {
        targeted.strategies = targeted.strategies.filter(s =>
          s.name.slice(0, 3) !== 'ACT' && s.name.slice(0, 3) !== 'HDM');
      }
    }
    let flatFeePkg = false;
    if (pkg && pkg.rateType === 'Flat Fee') {
      flatFeePkg = true;
      targeted.strategies = targeted.strategies.filter(s => s.SalesProducts.some(sp =>
        sp.StrategySalesProducts.flatFeeEligible));
    }
    if (!placement || isEmpty(placement)) {
      return targeted;
    }
    if (placement.Strategy && salesProducts) {
      targeted.salesProducts = salesProducts.filter(sp => sp.Strategies.find(s => s.id === placement.Strategy.id));
      if (flatFeePkg) {
        targeted.salesProducts = targeted.salesProducts.filter(sp =>
          sp.Strategies.some(s => s.StrategySalesProducts.flatFeeEligible));
      }
      if (order && order.orderType !== 'Programmatic') {
        remove(targeted.salesProducts, sp => sp.name === 'Hearst O&O Inventory');
      }
    }
    targeted.presetTypeId = this.getPresetTypeId(placement.StrategyId, placement.SalesProductId);
    if (placement.Strategy && placement.Strategy.RequiredDetailTypeId && presets) {
      targeted.presets = presets.filter(p => p.CustomCriteriaPresetType.id === targeted.presetTypeId);
    }
    if (placement.SalesProduct) {
      const targetSalesProductIds = targeted.salesProducts.map(sp => sp.id);
      if (!targetSalesProductIds.includes(placement.SalesProduct.id)) {
        placement.SalesProductId = null;
        placement.SalesProduct = {};
      }
    }
    targeted.creativeProducts = (order && order.orderType !== 'Programmatic') ?
      creativeProducts.filter(cp => cp.progRule !== 'progOnly') : creativeProducts;
    if (!isEmpty(placement.Strategy) && !isEmpty(placement.SalesProduct)) {
      const icps = constants.invalidCreativeProducts.filter(icp => icp.strat === placement.Strategy.name &&
        icp.sp === placement.SalesProduct.name);
      icps.forEach(icp => {
        targeted.creativeProducts = targeted.creativeProducts.filter(cp => cp.name !== icp.cp);
      });
      // console.log(icps);
    }
    // Look up the strategy and sales product to find the model exclusion status
    if (doExclusions) {
      if (!isEmpty(placement.Strategy) && !isEmpty(placement.SalesProduct)) {
        const thisStrategy = strategies.find(s => s.name === placement.Strategy.name);
        const thisSP = thisStrategy.SalesProducts.find(sp => sp.name === placement.SalesProduct.name);
        this.canExcludeModels = thisSP.StrategySalesProducts.modelExclusionEligible;
      }
    }
    if (placement.SalesProduct && placement.DeviceTypes.length > 0) {
      targeted.creativeProducts =
        targeted.creativeProducts.filter(cp => cp.SalesProducts.find(sp => sp.id === placement.SalesProduct.id));
      const pdtIds = placement.DeviceTypes.map(dt => dt.id);
      targeted.creativeProducts =
        targeted.creativeProducts.filter(cp => cp.DeviceTypes.find(dt => pdtIds.includes(dt.id)));
    }
    if (placement.CreativeProducts && !isDelivering(placement)) {
      const targetCreativeProductIds = targeted.creativeProducts.map(cp => cp.id);
      placement.CreativeProducts = placement.CreativeProducts.filter(pcp => targetCreativeProductIds.includes(pcp.id));
    }
    return targeted;
  }

  manageLanguages(order) {
    const languageChoices = ['English'];
    if (order && order.Advertiser && order.Advertiser.countryCode === 'CDN') {
      languageChoices.push('French Browser');
    } else {
      languageChoices.push('Spanish Browser');
    }
    return languageChoices;
  }

  clearPubs = () => {
    const copy = Object.assign({}, this.props.placement);
    copy.Pubs = [];
    this.props.onChange({ target: { name: 'Pubs', value: copy.Pubs } });
    this.setState({ pubsCleared: true });
  }

  switchChange = () => {
    const excludeModels = !this.state.excludeModels;
    this.setState({ excludeModels });
  }

  render() {
    const {
      errors,
      classes,
      onChange,
      onExclusionsChange,
      placement,
      order,
      isFetching,
      strategies,
      deviceTypes,
      salesProducts,
      creativeProducts,
      presets,
      tpts,
      pkg,
      constants
    } = this.props;
    if (typeof placement.Pubs === 'string') {
      placement.Pubs = placement.Pubs.split(';');
    }
    const languageChoices = this.manageLanguages(order);
    const showGlobal = true;
    const targeted = this.manageCascade(order, placement, strategies, salesProducts, presets, creativeProducts, pkg, constants);
    return <>
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore/>}>
        <GpsFixed className={classes}/>
        <Typography variant="subtitle1">Standard Targeting</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isFetching && <p>Fetching...</p>}
        {!isFetching && !isEmpty(strategies) && !isEmpty(presets) && !isEmpty(creativeProducts) && (
          <Grid container spacing={3} direction="row" justifyContent="center" alignItems="flex-start">
            <Grid item xs={6}>
              <Strategy
                value={placement.StrategyId || ''}
                strategies={targeted.strategies}
                disabled={false}
                onChange={this.strategyChanged}
              />
              <FormHelperText error>{errors.StrategyId}</FormHelperText>
            </Grid>

            <Grid item xs={6}>
              <SalesProduct
                value={placement.SalesProductId || ''}
                disabled={false}
                strategy={placement.Strategy}
                orderType={order.orderType}
                salesProducts={targeted.salesProducts}
                priorSalesProduct={placement && placement.priorValues && placement.priorValues.SalesProduct}
                constants={constants}
                isPushed={isPushed(placement)}
                onChange={this.salesProductChanged}
              />
              <FormHelperText error>{errors.SalesProductId}</FormHelperText>
            </Grid>

            {!!targeted.presetTypeId && targeted.presetTypeId !== 'unused' && (
              <Grid item xs={12}>
                <StrategyDetails
                  presetType={targeted.presetTypeId}
                  presets={targeted.presets}
                  disabled={false}
                  onChange={onChange}
                  value={placement.CustomCriteriaPresets}
                />
                <FormHelperText error>{errors.CustomCriteriaPresets}</FormHelperText>
              </Grid>
            )}

            {this.canExcludeModels && (
              <>
              <Grid item xs={2}>
                <FormControlLabel control={<Checkbox onChange={this.excludeChange} />} label="Exclusions" />
              </Grid>
              {(this.state.excludeModels || !isEmpty(placement.exclusions)) && (
                <Grid item xs={10}>
                  <ExcludedModels
                    disabled={false}
                    onChange={onExclusionsChange}
                    presets={presets}
                    value={placement.exclusions}
                  />
                <FormHelperText error>{errors.CustomCriteriaPresets}</FormHelperText>
              </Grid>
                )}
              {!this.excludeModels && (
                <Grid item xs={10}>
                </Grid>
              )}
              </>
            )}

            <Grid item xs={4}>
              <DeviceTypes
                value={placement.DeviceTypes}
                onChange={onChange}
                deviceTypes={deviceTypes}
              />
              <FormHelperText error>{errors.DeviceTypes}</FormHelperText>
            </Grid>

            <Grid item xs={8}>
              <CreativeProducts
                value={placement.CreativeProducts}
                creativeProducts={targeted.creativeProducts}
                creativeSource={order.creativeSource}
                orderType={order.orderType}
                onChange={onChange}
              />
              <FormHelperText error>{errors.CreativeProducts}</FormHelperText>
            </Grid>

            {hasPubSelect(placement.SalesProduct, placement.Strategy) && (
              <>
                <Grid item xs={10}>
                  <PubSelect
                    currentPubs={placement.Pubs}
                    onChange={onChange}
                    disabled={false}
                    pubsCleared={this.state.pubsCleared}
                    constants={constants}
                    strategyName={placement.Strategy.name}
                    selectType={hasPubSelect(placement.SalesProduct, placement.Strategy)}
                    isNew={!placement.id}
                  />
                  <FormHelperText error>{errors.Pubs}</FormHelperText>
                </Grid>
                <Grid item xs={2}>
                <Button variant="contained" onClick={this.clearPubs}>
                  Clear Publishers
                </Button>
                </Grid>
              </>
            )}
            <Grid item xs={2}>
              <TextField
                variant="standard"
                select
                fullWidth
                name="packageType"
                onChange={onChange}
                margin="normal"
                label="Package Type"
                value={placement.packageType || order.packageType || 'In-Market'}
                disabled
                SelectProps={{
                  MenuProps: {
                    className: classes.menu
                  }
                }}>
                {constants.packageTypes.map(pt => (
                  <MenuItem key={pt} value={pt}>{pt}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {order && order.Advertiser && order.Advertiser.countryCode === 'CDN' && (
              <>
                <Grid item xs={2}>
                  <TextField
                    variant="standard"
                    select
                    fullWidth
                    name="targetLanguage"
                    onChange={onChange}
                    margin="normal"
                    label="Target Language"
                    value={placement.targetLanguage || 'English'}
                    SelectProps={{
                      MenuProps: {
                        className: classes.menu
                      }
                    }}>
                    {languageChoices.map(lc => (
                      <MenuItem key={lc} value={lc}>{lc}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}/>
              </>
            )}
            {(order && order.Advertiser && order.Advertiser.countryCode !== 'CDN') && (
              <Grid item xs={2}>
                <TextField
                  variant="standard"
                  select
                  fullWidth
                  name="targetLanguage"
                  onChange={onChange}
                  margin="normal"
                  label="Target Language"
                  value={placement.targetLanguage || 'English'}
                  SelectProps={{
                    MenuProps: {
                      className: classes.menu
                    }
                  }}>
                  {languageChoices.map(lc => (
                    <MenuItem key={lc} value={lc}>{lc}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={2}>
              {showGlobal && <FormControlLabel
                control={<Checkbox
                  checked={placement.geoGlobal}
                  onChange={this.geoGlobalChange}
                  value={placement.geoGlobal} />} label="Global Targeting" />
              }
            </Grid>
            <Grid item xs={6}/>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore/>}>
        <GpsFixed className={classes}/>
        <Typography variant="subtitle1">Custom Targeting</Typography>
        <span>&nbsp; &nbsp;&nbsp; &nbsp;</span>
        {placement.pushStatus === 'OVER_TARGETED' && (
          <>
        <Chip label={placement.pushStatus} color={'error'}></Chip>
            <span>&nbsp; &nbsp;&nbsp; &nbsp;</span>
            <span>Targeting has too many criteria. The limit is 800 for custom and standard criteria; this has {placement.LineItems[0].adManagerPayload.criteriaCount || '?'}</span>
          </> )}
      </AccordionSummary>
      <AccordionDetails>
        {isFetching && <p>Fetching...</p>}
        {!isFetching && !isEmpty(strategies) && !isEmpty(presets) && !isEmpty(creativeProducts) && (
          <Grid container spacing={3} direction="row" justifyContent="center" alignItems="flex-start">
            <GenericTargeting
              tpts={tpts}
              placement={placement}
              onChange={this.tptChanged}
            />
          </Grid>
          )}
        </AccordionDetails>
    </Accordion>
    </>;
  }
}

Targeting.defaultProps = {
  isFetching: false,
  order: null,
  strategies: [],
  salesProducts: [],
  creativeProducts: [],
  deviceTypes: [],
  presets: []
};

Targeting.propTypes = {
  isFetching: PropTypes.bool,
  classes: PropTypes.string.isRequired,
  placement: PropTypes.object.isRequired,
  order: PropTypes.object,
  strategies: PropTypes.array,
  salesProducts: PropTypes.arrayOf(PropTypes.object),
  creativeProducts: PropTypes.arrayOf(PropTypes.object),
  deviceTypes: PropTypes.arrayOf(PropTypes.object),
  presets: PropTypes.array,
  constants: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { dummy } = state;
  return { dummy }
};

export default connect(mapStateToProps)(Targeting);
